import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  TPageConfig,
  TSelectionPage,
  TWeightLossPage,
  TGetItemFn,
  TSetLastVisitedItemFn,
} from '@/config/types'
import { PageName } from '@/enums/pageName'
import { getGoalWeightByDate, getWeightFormat } from '@/utils/weightUtils'

import { PrimaryButton } from '@/components/buttons/primaryButton'
import { PageTitle } from '@/components/pageTitle'
import { FooterPolicy } from '@/components/policyLinkBlock'
import { Vector } from '@/shared/icons/Vector'

import { useAuth } from '@/auth/useAuth'
import { useSession } from '@/core/session/useSession'
import { trackPageView, trackPageComplete } from '@/events/events'

type TWeightLossProgramProps = {
  pageName: PageName.weightLossProgram
  getItem: TGetItemFn
  setLastVisitedItem: TSetLastVisitedItemFn
  pageConfig: TWeightLossPage
  config: IConfig
  pagesConfig: TPageConfig<TSelectionPage>
}

export const WeightLossProgram = ({
  pageName,
  pageConfig,
  setLastVisitedItem,
  getItem,
  config,
  pagesConfig,
}: TWeightLossProgramProps) => {
  const { user } = useAuth()
  const sessionContext = useSession()
  const navigate = useNavigate()

  const {
    title,
    weightDescription,
    yourGoalTitle,
    options,
    nextUrl,
    CustomChart,
    policyBlock = false,
    buttonName,
  } = pageConfig
  const currentWeight = JSON.parse(getItem(PageName.weight) || '')
  const weightGoal = JSON.parse(getItem(PageName.weightGoal) || '')

  const weightFormat = getWeightFormat(currentWeight.measurementSystem)

  const dateToLoseWeight = getGoalWeightByDate(
    weightFormat,
    parseInt(currentWeight?.value),
    parseInt(weightGoal.value)
  )

  const goalDate = yourGoalTitle.replace('{endDate}', dateToLoseWeight)

  useEffect(
    () => {
      trackPageView(
        pageName,
        config.PARTNER_ID,
        config.APP_ID,
        config.PAGE_SET_ID,
        sessionContext,
        user
      )
      document.title = title
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const nextPage = () => {
    setLastVisitedItem(pageName)
    trackPageComplete(
      pageName,
      [],
      config.PARTNER_ID,
      config.APP_ID,
      config.PAGE_SET_ID,
      sessionContext,
      user
    )
    navigate(nextUrl)
  }

  const className = `page page-survey page-${pageName}`
  return (
    <div className={className}>
      <PageTitle title={title} />
      <div className="content-wrapper">
        <div className="page-subtitle">{weightDescription}</div>
        {CustomChart && <CustomChart getItem={getItem} pagesConfig={pagesConfig} />}
        <div className="info-block">{goalDate}</div>
        <div className="features-list">
          {options?.map((option) => {
            return (
              <div className="feature-item" key={option.title}>
                <Vector />
                <div className="text">{option.title}</div>
              </div>
            )
          })}
        </div>
      </div>
      <PrimaryButton className="continue-btn" onClick={nextPage}>
        {buttonName || 'Continue'}
      </PrimaryButton>
      {policyBlock && <FooterPolicy />}
    </div>
  )
}
